"use client";

import { cn } from "@/lib/cn";
import { useState, type ReactNode } from "react";

export interface TabItem {
  value: string;
  label: ReactNode;
}

export interface TabsProps {
  items: TabItem[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  className?: string;
  /** "underline" (default) o "pill" (segmented control, ideal en mobile). */
  variant?: "underline" | "pill";
}

/**
 * Tabs accesible. Controlado (pasá `value` + `onValueChange`) o no controlado
 * (`defaultValue`). Sólo renderiza la barra de tabs; el contenido lo maneja la
 * superficie según el valor activo.
 */
export function Tabs({
  items,
  value,
  defaultValue,
  onValueChange,
  className,
  variant = "underline",
}: TabsProps) {
  const [internal, setInternal] = useState(defaultValue ?? items[0]?.value);
  const active = value ?? internal;

  const select = (v: string) => {
    if (value === undefined) setInternal(v);
    onValueChange?.(v);
  };

  if (variant === "pill") {
    return (
      <div
        role="tablist"
        className={cn(
          // `max-w-full` + `overflow-x-auto` — con muchos items (ej. el editor
          // de perfil de terapeuta, 4 tabs) el segmented control puede ser más
          // ancho que el viewport en mobile; sin esto el resto quedaba
          // inalcanzable (cortado por el borde, sin forma de deslizar).
          "flex max-w-full gap-1 overflow-x-auto rounded-full bg-surface-sunk p-1",
          className,
        )}
      >
        {items.map((it) => {
          const on = it.value === active;
          return (
            <button
              key={it.value}
              role="tab"
              aria-selected={on}
              onClick={() => select(it.value)}
              className={cn(
                "shrink-0 whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
                on
                  ? "bg-surface text-ink shadow-warm-sm"
                  : "text-ink-muted hover:text-ink-soft",
              )}
            >
              {it.label}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div
      role="tablist"
      className={cn(
        "flex gap-6 border-b border-sand-300 overflow-x-auto",
        className,
      )}
    >
      {items.map((it) => {
        const on = it.value === active;
        return (
          <button
            key={it.value}
            role="tab"
            aria-selected={on}
            onClick={() => select(it.value)}
            className={cn(
              "relative -mb-px whitespace-nowrap border-b-2 px-0.5 py-3 text-sm font-medium transition-colors",
              on
                ? "border-forest text-ink"
                : "border-transparent text-ink-muted hover:text-ink-soft",
            )}
          >
            {it.label}
          </button>
        );
      })}
    </div>
  );
}
